import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Volume2, StopCircle } from 'lucide-react';
import { fetchAudioWithTimings } from '../../services/api';
import KaraokeText from '../KaraokeText';
import ExerciseCard from '../shared/ExerciseCard';
import TranslatableText from '../shared/TranslatableText';

const Conversation = ({ conversation, topic }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentLineIndex, setCurrentLineIndex] = useState(-1);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [currentWordTimings, setCurrentWordTimings] = useState([]);
  const stopRequestedRef = useRef(false);
  const audioRef = useRef(null);
  const animationFrameRef = useRef(null);
  const blobUrlRef = useRef(null);

  const getSpeakerForGender = (gender) => {
    return gender === 'female' ? 'af_sarah' : 'am_michael';
  };

  // Update current word based on audio time
  const updateCurrentWord = useCallback(() => {
    if (!audioRef.current || currentWordTimings.length === 0) {
      if (isPlaying) {
        animationFrameRef.current = requestAnimationFrame(updateCurrentWord);
      }
      return;
    }

    const currentTime = audioRef.current.currentTime;

    // Find the word at current time
    let foundIndex = -1;
    for (let i = 0; i < currentWordTimings.length; i++) {
      const timing = currentWordTimings[i];
      if (currentTime >= timing.start && currentTime < timing.end) {
        foundIndex = i;
        break;
      }
    }

    setCurrentWordIndex(foundIndex);

    if (isPlaying && audioRef.current && !audioRef.current.paused) {
      animationFrameRef.current = requestAnimationFrame(updateCurrentWord);
    }
  }, [currentWordTimings, isPlaying]);

  // Start animation frame when playing
  useEffect(() => {
    if (isPlaying && currentWordTimings.length > 0) {
      animationFrameRef.current = requestAnimationFrame(updateCurrentWord);
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, updateCurrentWord, currentWordTimings]);

  const cleanup = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  const stopPlayback = () => {
    stopRequestedRef.current = true;
    cleanup();
    setIsPlaying(false);
    setCurrentLineIndex(-1);
    setCurrentWordIndex(-1);
    setCurrentWordTimings([]);
  };

  const playConversation = async () => {
    try {
      setIsPlaying(true);
      stopRequestedRef.current = false;

      for (let i = 0; i < conversation.length; i++) {
        if (stopRequestedRef.current) break;

        const { text, gender } = conversation[i];
        setCurrentLineIndex(i);
        setCurrentWordIndex(-1);

        const speaker = getSpeakerForGender(gender);

        // Fetch audio with word timings
        const { audioBlob, wordTimings } = await fetchAudioWithTimings(text, speaker);

        if (stopRequestedRef.current) break;

        // Set word timings for karaoke display
        setCurrentWordTimings(wordTimings);

        // Cleanup previous audio
        cleanup();

        // Create new audio
        blobUrlRef.current = URL.createObjectURL(audioBlob);
        audioRef.current = new Audio(blobUrlRef.current);

        // Play and wait for completion
        await new Promise((resolve, reject) => {
          audioRef.current.onended = () => {
            resolve();
          };
          audioRef.current.onerror = (err) => {
            reject(err);
          };
          audioRef.current.play().catch((err) => {
            reject(err);
          });
        });

        // Reset word highlighting after line finishes
        setCurrentWordIndex(-1);
        setCurrentWordTimings([]);
      }

      cleanup();
      setCurrentLineIndex(-1);
      setCurrentWordIndex(-1);
      setCurrentWordTimings([]);
      setIsPlaying(false);
    } catch (error) {
      console.error('Error:', error);
      cleanup();
      setIsPlaying(false);
      setCurrentLineIndex(-1);
      setCurrentWordIndex(-1);
      setCurrentWordTimings([]);
    }
  };

  return (
    <ExerciseCard>
      <h2 className="text-2xl font-bold text-gray-800 border-b-4 border-blue-500 pb-4 mb-4">
        Přečtěte si konverzaci
      </h2>

      <div className="flex gap-3 justify-center mb-5">
        <button
          onClick={playConversation}
          disabled={isPlaying}
          className={`px-5 py-2.5 rounded-lg flex items-center gap-2 font-medium transition-all ${
            isPlaying
              ? 'bg-amber-500 text-white cursor-default opacity-80'
              : 'bg-cyan-500 hover:bg-cyan-600 text-white cursor-pointer'
          }`}
        >
          <Volume2 size={20} />
          {isPlaying ? 'Přehráva se...' : 'Poslechnout konverzaci'}
        </button>
        {isPlaying && (
          <button
            onClick={stopPlayback}
            className="px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center gap-2 font-medium transition-all"
          >
            <StopCircle size={20} />
            Stop
          </button>
        )}
      </div>

      <div className="text-lg leading-relaxed">
        {conversation.map(({ role, text, gender }, index) => (
          <div
            key={index}
            className={`px-3 py-2 rounded-lg mb-1 transition-colors ${
              currentLineIndex === index ? 'bg-blue-50' : ''
            }`}
          >
            <strong className={`text-xl ${gender === 'female' ? 'text-rose-600' : 'text-blue-600'}`}>
              {role}:
            </strong>{' '}
            {currentLineIndex === index && currentWordTimings.length > 0 ? (
              <KaraokeText
                wordTimings={currentWordTimings}
                currentWordIndex={currentWordIndex}
                textColor="#374151"
                highlightColor="#fef3c7"
              />
            ) : (
              <TranslatableText text={text} textColor="#374151" topic={topic} />
            )}
          </div>
        ))}
      </div>
    </ExerciseCard>
  );
};

export default Conversation;
